/**
 * Twemoji rendering as React nodes.
 *
 * Deliberately free of the `twemoji` npm package (~490KB) — that is only
 * needed to rewrite raw HTML, which `parseTwemojiHtml` in ./twemoji handles.
 * Keeping the node renderer separate lets the dock widget use it without
 * dragging the parser into the entry chunk, where the widget is registered.
 */

import { useMemo, useState, type ReactNode } from "react";

export const TWEMOJI_BASE =
  "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg";

const EMOJI_PATTERN =
  /(\p{Regional_Indicator}{2}|(?:\p{Emoji}\uFE0F|\p{Emoji_Presentation})(?:\u200D(?:\p{Emoji}\uFE0F|\p{Emoji_Presentation}))*)/gu;

function emojiCodepoint(emoji: string): string {
  return Array.from(emoji)
    .map((char) => char.codePointAt(0)?.toString(16))
    .filter(Boolean)
    .join("-");
}

function emojiCandidates(emoji: string): string[] {
  const withoutVariation = emoji.replace(/\ufe0f/g, "");
  return Array.from(new Set([withoutVariation, emoji])).map(
    (candidate) => `${TWEMOJI_BASE}/${emojiCodepoint(candidate)}.svg`
  );
}

export function twemojiUrl(emoji: string): string {
  return emojiCandidates(emoji)[0];
}

function TwemojiImage({ emoji }: { emoji: string }) {
  const urls = useMemo(() => emojiCandidates(emoji), [emoji]);
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState(false);
  const src = urls[index];

  if (!src || failed) return <>{emoji}</>;

  return (
    <img
      src={src}
      alt={emoji}
      draggable={false}
      className="mx-[0.03em] inline-block h-[1.2em] w-[1.2em] align-[-0.18em]"
      onError={() => {
        if (index < urls.length - 1) {
          setIndex((value) => value + 1);
          return;
        }
        setFailed(true);
      }}
    />
  );
}

export function renderTwemojiNodes(
  text: string,
  keyPrefix = "emoji"
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(EMOJI_PATTERN)) {
    const emoji = match[0];
    const start = match.index ?? 0;
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    nodes.push(<TwemojiImage key={`${keyPrefix}-${start}-${emoji}`} emoji={emoji} />);
    lastIndex = start + emoji.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes.length > 0 ? nodes : [text];
}

export function TwemojiText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return <span className={className}>{renderTwemojiNodes(text)}</span>;
}
